import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";

import {
  getNomenclatureInfo,
  type NomenclatureInfo,
  type RefName,
  type StockRow,
  type UnitKind,
} from "@/api/label_info";
import { LoadState } from "@/lib/scan-ui/screen";
import { fmtQty } from "@/lib/scan-ui/status";
import { cn } from "@/lib/utils";

/** Подпись вида структурной единицы: не-склады бэкенд НЕ отбрасывает, а
 *  маркирует — вопрос раздела «где лежит вообще». */
export const UNIT_KIND_LABEL: Record<UnitKind, string> = {
  warehouse: "склад",
  department: "подразделение",
  counterparty: "у комиссионера",
  other: "прочее",
};

/** Юрлиц не больше стольких — развёрнуты сразу; больше — свёрнуты. */
const EXPAND_ORGS_UP_TO = 3;

/**
 * Карточка номенклатуры: остаток (всего / свободно / резерв) с разбивкой
 * юрлицо → единица → характеристика · партия и расход за 90 дней.
 *
 * `scanId` — в ключе запроса: повторный скан того же товара перечитывает
 * остаток (горячий запрос), а не показывает кэш прошлого.
 */
export function NomenclatureCard({
  nomenclatureRef,
  characteristicRef,
  partia,
  scanId,
}: {
  nomenclatureRef: string;
  characteristicRef: string;
  /** Партия со скана (GS1 AI10, ЧЗ) — `null`, если код её не несёт. Ручка
   *  карточки партию не принимает: остаток нужен по всем партиям, а эта лишь
   *  подписывается в шапке и подсвечивается в строках. */
  partia: RefName | null;
  scanId: number;
}): React.ReactElement {
  const q = useQuery({
    queryKey: ["label-info-nomenclature", nomenclatureRef, characteristicRef, scanId],
    queryFn: () => getNomenclatureInfo(nomenclatureRef, characteristicRef),
  });
  if (!q.data) {
    return (
      <LoadState
        isLoading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        className="flex flex-col items-center justify-center gap-3 rounded-xl border bg-white p-6 text-center"
      />
    );
  }
  return <CardBody info={q.data} partia={partia} />;
}

interface UnitGroup {
  unit: RefName;
  kind: UnitKind;
  rows: StockRow[];
}

interface OrgGroup {
  organization: RefName;
  total: number;
  free: number;
  reserved: number;
  units: UnitGroup[];
}

/** Строки уже отсортированы бэком (юрлицо, склады первыми, имя) — группируем
 *  с сохранением порядка; юрлица — по убыванию остатка. */
function groupRows(rows: StockRow[]): OrgGroup[] {
  const orgs = new Map<string, OrgGroup>();
  for (const r of rows) {
    let org = orgs.get(r.organization.ref);
    if (!org) {
      org = { organization: r.organization, total: 0, free: 0, reserved: 0, units: [] };
      orgs.set(r.organization.ref, org);
    }
    org.total += r.total;
    org.free += r.free;
    org.reserved += r.reserved;
    let unit = org.units.find((u) => u.unit.ref === r.unit.ref);
    if (!unit) {
      unit = { unit: r.unit, kind: r.unit_kind, rows: [] };
      org.units.push(unit);
    }
    unit.rows.push(r);
  }
  return [...orgs.values()].sort((a, b) => b.total - a.total);
}

function CardBody({
  info,
  partia,
}: {
  info: NomenclatureInfo;
  partia: RefName | null;
}): React.ReactElement {
  const n = info.nomenclature;
  const unitName = n.unit?.name ?? "";
  const orgs = groupRows(info.stock.rows);
  const highlight: Highlight = {
    characteristicRef: info.characteristic?.ref ?? "",
    partiaRef: partia?.ref ?? "",
  };

  return (
    <div className="space-y-2" data-testid="nomenclature-card">
      <div className="rounded-xl border bg-white p-3">
        <div className="font-mono text-[18px] font-bold">{n.article || "—"}</div>
        <div className="text-[14px] leading-snug text-slate-800">{n.name}</div>
        <div className="mt-1 flex flex-wrap gap-x-3 text-[12px] text-slate-500">
          {unitName && <span>ед.: {unitName}</span>}
          {info.characteristic && (
            <span className="rounded bg-amber-100 px-1.5 font-medium text-amber-900">
              {info.characteristic.name}
            </span>
          )}
          {partia && (
            <span
              data-testid="scan-partia"
              className="rounded bg-amber-100 px-1.5 font-medium text-amber-900"
            >
              партия {partia.name}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid="stock-totals">
        <Total label="Всего" value={info.stock.total} tone="text-slate-900" />
        <Total label="Свободно" value={info.stock.free} tone="text-emerald-700" />
        <Total label="Резерв" value={info.stock.reserved} tone="text-amber-700" />
      </div>

      {info.warnings.length > 0 && (
        <div className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-900">
          {info.warnings.map((w) => (
            <div key={w} className="flex gap-1.5">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {orgs.length === 0 ? (
        <p className="rounded-xl border bg-white p-4 text-center text-[14px] text-slate-500">
          Остатка нет ни у одного юрлица
        </p>
      ) : (
        orgs.map((org) => (
          <OrgSection
            key={org.organization.ref}
            org={org}
            defaultOpen={orgs.length <= EXPAND_ORGS_UP_TO}
            highlight={highlight}
          />
        ))
      )}

      <Consumption info={info} />
    </div>
  );
}

function Total({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: string;
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-white px-2 py-2 text-center">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={cn("font-mono text-[26px] font-bold leading-tight", tone)}>{fmtQty(value)}</div>
    </div>
  );
}

/** Что подсвечивать в строках остатка: характеристика и партия со скана
 *  (пустая строка — скан её не определил, по ней не фильтруем). */
interface Highlight {
  characteristicRef: string;
  partiaRef: string;
}

/** Строка подсвечивается, если совпала со всем, что скан определил; скан без
 *  характеристики и партии не подсвечивает ничего. */
function isHighlighted(row: StockRow, h: Highlight): boolean {
  if (!h.characteristicRef && !h.partiaRef) return false;
  if (h.characteristicRef && row.characteristic?.ref !== h.characteristicRef) return false;
  if (h.partiaRef && row.partia?.ref !== h.partiaRef) return false;
  return true;
}

function OrgSection({
  org,
  defaultOpen,
  highlight,
}: {
  org: OrgGroup;
  defaultOpen: boolean;
  highlight: Highlight;
}): React.ReactElement {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border bg-white" data-testid="stock-org">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 p-3 text-left"
      >
        {open ? (
          <ChevronDown className="size-5 shrink-0 text-slate-500" />
        ) : (
          <ChevronRight className="size-5 shrink-0 text-slate-500" />
        )}
        <span className="min-w-0 flex-1 truncate text-[15px] font-bold">{org.organization.name}</span>
        <span className="text-right leading-tight">
          <span className="block font-mono text-[18px] font-bold">{fmtQty(org.total)}</span>
          <span className="block text-[11px] text-slate-500">
            св. {fmtQty(org.free)} · рез. {fmtQty(org.reserved)}
          </span>
        </span>
      </button>
      {open && (
        <div className="space-y-2 border-t px-3 pb-3 pt-2">
          {org.units.map((u) => (
            <div key={u.unit.ref}>
              <div className="flex items-baseline gap-2">
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{u.unit.name}</span>
                <span
                  className={cn(
                    "shrink-0 rounded px-1.5 text-[10px] font-bold",
                    u.kind === "warehouse" ? "bg-slate-100 text-slate-600" : "bg-violet-100 text-violet-800",
                  )}
                >
                  {UNIT_KIND_LABEL[u.kind]}
                </span>
              </div>
              <div className="mt-1 space-y-1">
                {u.rows.map((r, i) => (
                  <StockLine
                    key={`${r.characteristic?.ref ?? ""}:${r.partia?.ref ?? ""}:${i}`}
                    row={r}
                    highlighted={isHighlighted(r, highlight)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StockLine({
  row,
  highlighted,
}: {
  row: StockRow;
  highlighted: boolean;
}): React.ReactElement {
  const label =
    [row.characteristic?.name, row.partia && `партия ${row.partia.name}`].filter(Boolean).join(" · ") ||
    "без характеристики и партии";
  return (
    <div
      data-testid="stock-line"
      data-highlighted={highlighted ? "true" : undefined}
      className={cn(
        "flex items-center gap-2 rounded-md px-2 py-1 text-[13px]",
        highlighted ? "bg-amber-100 ring-1 ring-amber-400" : "bg-slate-50",
      )}
    >
      <span className="min-w-0 flex-1 text-slate-700">{label}</span>
      <span className="shrink-0 text-right font-mono leading-tight">
        <span className="font-bold text-emerald-700">{fmtQty(row.free)}</span>
        {row.reserved > 0 && (
          <span className="text-amber-700"> + рез. {fmtQty(row.reserved)}</span>
        )}
      </span>
    </div>
  );
}

function Consumption({ info }: { info: NomenclatureInfo }): React.ReactElement {
  const c = info.consumption;
  return (
    <div className="rounded-xl border bg-white p-3" data-testid="consumption">
      <div className="flex items-baseline justify-between">
        <span className="text-[14px] font-bold">Расход за {c.days} дней</span>
        <span className="font-mono text-[22px] font-bold">{fmtQty(c.total)}</span>
      </div>
      <div className="mt-1 text-[14px] font-semibold">
        {info.days_of_cover === null
          ? "Расхода не было"
          : `Хватит на ≈ ${fmtQty(Math.round(info.days_of_cover))} дн.`}
      </div>
      <div className="text-[11px] text-slate-500">
        На складах {fmtQty(info.stock.on_warehouses)}; расход — со складов по всем юрлицам, без
        перемещений между своими юрлицами.
      </div>
      {c.by_organization.length > 0 && (
        <div className="mt-2 space-y-0.5 border-t pt-2">
          {c.by_organization.map((o) => (
            <div key={o.organization.ref} className="flex justify-between gap-2 text-[13px]">
              <span className="min-w-0 truncate text-slate-700">{o.organization.name}</span>
              <span className="shrink-0 font-mono font-semibold">{fmtQty(o.qty)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
